// src/components/Header.jsx
// -----------------------------------------------------------------------------
// The header is the primary navigation surface. It now hosts the theme toggle
// and uses the refreshed background tokens so it blends with the new shell.
import React from "react";
import { NavLink } from "react-router-dom";
import { Trophy } from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAccount } from "@/queries/account"; // samma hook som i AccountPage
import NotificationBell from "@/components/NotificationBell";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import { bottomNavItems } from "@/components/BottomNav";
import { useAccountAttention } from "@/hooks/useAccountAttention";
import LapstrTextLogo from "@/assets/Lapstr-text.png";
import { getNativePlatform } from "@/lib/runtime";

import NativeAppHeader from "@/components/native/NativeAppHeader";

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}




export default function Header() {
  const { isWidgetDragging, openShell } = useProtocolShell();
  // Kolla om vi är på /match/:id
  // const matchRoute = useMatch("/match/:id");
  // const activeMatchId = matchRoute?.params?.id || null;


  // // routes i headerMenyn
  // const nav = [
  //   { to: "/", label: "Tabell", show: true },
  //   { to: "/dashboard", label: "Dashboard", show: true },
  //   // { to: activeMatchId ? `/match/${activeMatchId}` : "/matches", label: "Protokoll", show: !!activeMatchId },
  // ];

  const { data: account } = useAccount();
  const { needsAccountAttention } = useAccountAttention();

  const initials =
    (account?.display_name || account?.email || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join("") || " ";

  if (getNativePlatform() === "android") {
    return (
      <NativeAppHeader
          account={account}
          initials={initials}
          isWidgetDragging={isWidgetDragging}
          needsAccountAttention={needsAccountAttention}
      />
    );
  }




  return (
    <header
      className={[
        "sticky top-0 z-40 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70 border-b border-border/60 shadow-[0_20px_40px_rgba(15,23,42,0.08)] transition-[transform,opacity,colors] duration-300 pt-[env(safe-area-inset-top)]",
        isWidgetDragging ? "-translate-y-full opacity-0 pointer-events-none" : "translate-y-0 opacity-100",
      ].join(" ")}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative min-h-16 flex items-center justify-between gap-4">
          {/* Brand */}
          <NavLink to="/" className="flex items-center space-x-3">
            {/* <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center"> */}
            {/* <div className="w-12 h-12 rounded-xl flex items-center justify-center">
              <img src="/icons/icon-512x512.png" alt="Logo" className="h-10 w-10 rounded-full" />
            </div> */}


            {/* <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center">
                <img
                  src="/icons/icon-512x512.png"
                  alt="Logo"
                  className="h-10 w-10 rounded-full"
                />
              </div>

              <img
                src={LapstrTextLogo}
                alt="Lapstr"
                className="h-[clamp(32px,3vw,32px)] w-auto shrink-0 -ml-3 mt-1"
              />
              <div className="hidden md:block text-xs text-muted-foreground -mt-0.5">
                Matchprotokoll för svensk speedway
              </div>
            </div> */}

            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0">
                <img
                  src="/icons/icon-512x512.png"
                  alt="Logo"
                  className="h-10 w-10 rounded-full"
                />
              </div>

              <div className="relative mt-1">
                <img
                  src={LapstrTextLogo}
                  alt="Lapstr"
                  className="h-[clamp(26px,4vw,36px)] w-auto max-w-none shrink-0 object-contain"
                />

                <div className="hidden md:block absolute left-0 -mt-1 text-xs text-muted-foreground whitespace-nowrap">
                  Matchprotokoll för svensk speedway
                </div>
              </div>
            </div>





            {/* <div className="hidden md:block">
              <div className="text-lg font-bold text-primary">Lapstr Speedway</div>
              <div className="text-xs text-muted-foreground -mt-0.5">
                Matchprotokoll för svensk speedway
              </div>
            </div> */}
          </NavLink>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 rounded-full bg-background/60 p-1 shadow-inner border border-border/40 absolute left-1/2 -translate-x-1/2">
            {bottomNavItems.map(({ id, to, label, icon: Icon, isCenter, disabled }) =>
              isCenter ? (
                <Button
                  key={id}
                  type="button"
                  variant="ghost"
                  data-tutorial-target="nav-new"
                  className="px-4 py-2 rounded-full text-sm font-medium text-muted-foreground hover:text-foreground"
                  onClick={openShell}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {label}
                </Button>
              ) : disabled ? (
                <div
                  key={id}
                  className="inline-flex items-center rounded-full px-4 py-2 text-sm font-medium text-muted-foreground/50"
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {label}
                </div>
              ) : (
                <NavLink
                  key={id}
                  to={to}
                  data-tutorial-target={
                    id === "home"
                      ? "nav-home"
                      : id === "live"
                        ? "nav-live"
                        : id === "table"
                          ? "nav-table"
                          : id === "series"
                            ? "nav-series"
                            : undefined
                  }
                  className={({ isActive }) =>
                    cx(
                      "inline-flex items-center rounded-full px-4 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-secondary/30 text-primary shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )
                  }
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {label}
                </NavLink>
              )
            )}
          </nav>

          {/* Auth (desktop) */}
          <div className="hidden lg:flex items-center gap-3">
            {account ? (
              <>
                <NotificationBell />
                <NavLink
                  data-tutorial-target="account"
                  to="/account"
                  className={({ isActive }) =>
                    `flex items-center gap-2 text-sm ${isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
                    }`
                  }
                >
                  {({ isActive }) => (
                  <span className="relative inline-flex">
                    <Avatar
                      className={`h-8 w-8 transition-shadow ${
                        isActive ? "ring-2 ring-[hsl(var(--primary))] ring-offset-2 ring-offset-background" : ""
                      }`}
                    >
                      <AvatarImage
                        src={account.avatar_url || ""}
                        alt={account.display_name || account.email || "User"}
                      />
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                    {needsAccountAttention ? (
                      <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-background bg-red-500" />
                    ) : null}
                  </span>
                  )}
                </NavLink>
              </>
            ) : (
              <NavLink to="/auth">
                <Button className="bg-[hsl(var(--primary))] hover:bg-[hsla(var(--primary),0.9)] shadow-md shadow-red-200/60 dark:shadow-none">
                  Logga in
                </Button>
              </NavLink>
            )}
          </div>

          {/* Auth (mobile: ikon endast) */}
          {/* <div className="sm:hidden flex items-center">
            {user && (
              <button
                onClick={handleLogout}
                aria-label="Logga ut"
                className="p-2 rounded-full text-muted-foreground hover:text-primary focus-visible:outline-none"
              // Om du inte har mapping för tokens: byt till
              // "text-[hsl(var(--muted))] hover:text-[hsl(var(--primary))]"
              >
                <LogOut className="w-5 h-5" />
              </button>
            )}
          </div> */}
          {/* Auth (mobile) */}
          <div className="lg:hidden flex items-center gap-3">
            {account && (
              <>
                <NotificationBell />
                <NavLink
                  data-tutorial-target="account"
                  to="/account"
                  aria-label="Konto"
                  className={({ isActive }) => (isActive ? "text-primary" : "")}
                >
                  {({ isActive }) => (
                  <span className="relative inline-flex">
                    <Avatar
                      className={`h-8 w-8 text-sm transition-shadow ${
                        isActive ? "ring-2 ring-[hsl(var(--primary))] ring-offset-2 ring-offset-background" : ""
                      }`}
                    >
                      <AvatarImage
                        src={account.avatar_url || ""}
                        alt={account.display_name || account.email || "User"}
                      />
                      <AvatarFallback>{initials}</AvatarFallback>
                    </Avatar>
                    {needsAccountAttention ? (
                      <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-background bg-red-500" />
                    ) : null}
                  </span>
                  )}
                </NavLink>
              </>
            )}
          </div>

          {/* (Mobilmeny tas om hand av bottomnav) */}
        </div>
      </div>
    </header>
  );
}


